import React, { useState, useEffect, useRef, useCallback } from 'react';
import { companyAPI, reviewAPI } from '../services/api';
import Icon from './Icon';

// Helper function to format address object to string
const formatAddressToString = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address;
  
  // If address is an object, extract and combine parts
  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.postcode || address.zip || address.postal_code) {
    parts.push(address.postcode || address.zip || address.postal_code);
  }
  if (address.state) parts.push(address.state);
  if (address.country) parts.push(address.country);
  
  return parts.join(', ');
};

function ShopBrowserModal({ isOpen, onClose, onSelectShop, darkMode = true, userLocation = null }) {
  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('distance'); // 'distance', 'rating', 'name'
  const [currentLocation, setCurrentLocation] = useState(userLocation);
  const [locationError, setLocationError] = useState('');
  const [showMap, setShowMap] = useState(true);
  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchShops();
      if (!currentLocation) {
        requestLocation();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    filterAndSortShops();
  }, [shops, searchTerm, sortBy, currentLocation]);

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationError('');
        },
        (error) => {
          console.warn('Location error:', error);
          setLocationError('Could not get your location. Sorting by distance unavailable.');
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }
  };

  const fetchShops = async () => {
    try {
      setLoading(true);
      const response = await companyAPI.getActiveCompanies();
      const companiesList = response.data.companies || [];
      
      // Filter out shops without online booking availability
      const bookableCompanies = companiesList.filter(company => 
        company.online_booking_availability_percent > 0
      );
      
      // Fetch ratings for each shop
      const shopsWithRatings = await Promise.all(
        bookableCompanies.map(async (company) => {
          try {
            const reviewsResp = await reviewAPI.getCompanyReviews(company.id);
            const reviews = reviewsResp.data.reviews || [];
            const avgRating = reviews.length > 0 
              ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
              : 0;
            return {
              ...company,
              rating: avgRating,
              reviewCount: reviews.length
            };
          } catch {
            return { ...company, rating: 0, reviewCount: 0 };
          }
        })
      );
      
      setShops(shopsWithRatings);
    } catch (err) {
      console.error('Error fetching shops:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const filterAndSortShops = useCallback(() => {
    let filtered = [...shops];

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(shop => {
        const addressStr = formatAddressToString(shop.address);
        return shop.name?.toLowerCase().includes(term) ||
          addressStr.toLowerCase().includes(term) ||
          shop.phone?.toLowerCase().includes(term) ||
          shop.email?.toLowerCase().includes(term);
      });
    }

    // Calculate distances if location available
    if (currentLocation) {
      filtered = filtered.map(shop => ({
        ...shop,
        distance: calculateDistance(
          currentLocation.lat, 
          currentLocation.lng,
          parseFloat(shop.latitude),
          parseFloat(shop.longitude)
        )
      }));
    }

    // Sort
    switch (sortBy) {
      case 'distance':
        if (currentLocation) {
          filtered.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
        }
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'name':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      default:
        break;
    }

    setFilteredShops(filtered);
  }, [shops, searchTerm, sortBy, currentLocation]);

  const formatDistance = (km) => {
    if (!km || km === Infinity) return null;
    if (km < 1) return `${Math.round(km * 1000)}m`;
    return `${km.toFixed(1)}km`;
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<span key={i} style={{ color: '#fbbf24' }}>★</span>);
      } else if (i === fullStars && hasHalf) {
        stars.push(<span key={i} style={{ color: '#fbbf24' }}>★</span>);
      } else {
        stars.push(<span key={i} style={{ color: 'rgba(255,255,255,0.2)' }}>★</span>);
      }
    }
    return stars;
  };

  // Initialize map when shops are loaded
  useEffect(() => {
    if (!isOpen || !showMap || loading || filteredShops.length === 0) return;
    
    const shopsWithCoords = filteredShops.filter(s => s.latitude && s.longitude);
    if (shopsWithCoords.length === 0) return;

    // Use Leaflet if available
    if (typeof window !== 'undefined' && window.L && mapRef.current) {
      // Clear existing map
      if (mapInstance) {
        mapInstance.remove();
      }

      // Calculate bounds
      const lats = shopsWithCoords.map(s => parseFloat(s.latitude));
      const lngs = shopsWithCoords.map(s => parseFloat(s.longitude));
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

      // Create map
      const map = window.L.map(mapRef.current).setView([centerLat, centerLng], 10);
      
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      // Add user location marker
      if (currentLocation) {
        window.L.marker([currentLocation.lat, currentLocation.lng], {
          icon: window.L.divIcon({
            className: 'user-location-marker',
            html: '<div style="width:16px;height:16px;background:#3b82f6;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>',
            iconSize: [16, 16],
            iconAnchor: [8, 8]
          })
        }).addTo(map).bindPopup('Your location');
      }

      // Add shop markers
      shopsWithCoords.forEach(shop => {
        const marker = window.L.marker([parseFloat(shop.latitude), parseFloat(shop.longitude)], {
          icon: window.L.divIcon({
            className: 'shop-marker',
            html: `<div style="width:32px;height:32px;background:#4A8C18;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;">🔧</div>`,
            iconSize: [32, 32],
            iconAnchor: [16, 32]
          })
        }).addTo(map);

        const shopAddressStr = formatAddressToString(shop.address);
        marker.bindPopup(`
          <div style="min-width:150px;">
            <strong>${shop.name}</strong>
            ${shop.rating > 0 ? `<br><span style="color:#fbbf24">${'★'.repeat(Math.round(shop.rating))}</span> (${shop.reviewCount})` : ''}
            ${shopAddressStr ? `<br><small>${shopAddressStr}</small>` : ''}
          </div>
        `);

        marker.on('click', () => {
          // Scroll to shop in list
          const shopElement = document.getElementById(`shop-${shop.id}`);
          if (shopElement) {
            shopElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            shopElement.style.background = 'rgba(61, 90, 32, 0.3)';
            setTimeout(() => {
              shopElement.style.background = '';
            }, 2000);
          }
        });
      });

      // Fit bounds if multiple shops
      if (shopsWithCoords.length > 1) {
        const bounds = window.L.latLngBounds(shopsWithCoords.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)]));
        if (currentLocation) {
          bounds.extend([currentLocation.lat, currentLocation.lng]);
        }
        map.fitBounds(bounds, { padding: [30, 30] });
      }

      setMapInstance(map);
    }

    return () => {
      if (mapInstance) {
        mapInstance.remove();
        setMapInstance(null);
      }
    };
  }, [isOpen, showMap, loading, filteredShops, currentLocation]);

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{ 
        position: 'fixed', 
        inset: 0, 
        background: 'rgba(0,0,0,0.8)', 
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div 
        className="modal modal-large"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '1000px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon name="companies" color="brand" darkMode={darkMode} size={24} />
            Find Service Shop
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '28px',
              cursor: 'pointer',
              padding: '0 8px'
            }}
          >
            ×
          </button>
        </div>

        {/* Search and Filters */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{ flex: '1 1 250px', position: 'relative' }}>
            <Icon 
              name="view" 
              color="secondary" 
              darkMode={darkMode} 
              size={16} 
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} 
            />
            <input
              type="text"
              placeholder="Search by name, address, city, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 36px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: '14px'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              <option value="distance">Distance</option>
              <option value="rating">Rating</option>
              <option value="name">Name</option>
            </select>
          </div>

          <button
            onClick={requestLocation}
            className="btn btn-secondary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Icon name="calendar" color="secondary" darkMode={darkMode} size={14} />
            Use My Location
          </button>

          <button
            onClick={() => setShowMap(!showMap)}
            className="btn btn-secondary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {showMap ? '📋 List Only' : '🗺️ Show Map'}
          </button>
        </div>

        {locationError && (
          <div style={{
            padding: '8px 24px',
            background: 'rgba(245, 158, 11, 0.1)',
            color: '#fbbf24',
            fontSize: '13px'
          }}>
            {locationError}
          </div>
        )}

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: showMap ? 'row' : 'column'
        }}>
          {/* Map */}
          {showMap && (
            <div 
              ref={mapRef}
              style={{
                flex: '1 1 50%',
                minHeight: '300px',
                background: 'rgba(0,0,0,0.2)'
              }}
            />
          )}

          {/* Shop List */}
          <div style={{
            flex: showMap ? '1 1 50%' : '1',
            overflowY: 'auto',
            padding: '16px'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                <Icon name="loading" color="secondary" darkMode={darkMode} size={32} />
                <p>Loading shops...</p>
              </div>
            ) : filteredShops.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                <Icon name="companies" color="muted" darkMode={darkMode} size={48} />
                <p>No shops found matching your search</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredShops.map(shop => (
                  <div
                    id={`shop-${shop.id}`}
                    key={shop.id}
                    onClick={() => onSelectShop(shop)}
                    style={{
                      padding: '16px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(61, 90, 32, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(61, 90, 32, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ 
                          margin: '0 0 6px 0', 
                          color: '#fff', 
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Icon name="companies" color="brand" darkMode={darkMode} size={18} />
                          {shop.name}
                        </h3>
                        
                        {/* Rating */}
                        {shop.rating > 0 && (
                          <div style={{ marginBottom: '6px', fontSize: '14px' }}>
                            {renderStars(shop.rating)}
                            <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: '6px' }}>
                              ({shop.reviewCount} {shop.reviewCount === 1 ? 'review' : 'reviews'})
                            </span>
                          </div>
                        )}

                        {/* Address */}
                        {(() => {
                          const addressStr = formatAddressToString(shop.address);
                          return addressStr && (
                            <p style={{ 
                              margin: '4px 0', 
                              color: 'rgba(255,255,255,0.6)', 
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <Icon name="location" size={14} customColor="rgba(255,255,255,0.6)" /> {addressStr}
                            </p>
                          );
                        })()}

                        {/* Phone */}
                        {shop.phone && (
                          <p style={{ 
                            margin: '4px 0', 
                            color: 'rgba(255,255,255,0.6)', 
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <Icon name="phone" size={14} customColor="rgba(255,255,255,0.6)" /> {shop.phone}
                          </p>
                        )}
                      </div>

                      {/* Distance badge */}
                      {shop.distance && shop.distance !== Infinity && (
                        <div style={{
                          background: 'rgba(59, 130, 246, 0.15)',
                          color: '#60a5fa',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatDistance(shop.distance)}
                        </div>
                      )}
                    </div>

                    {/* Subscription tier indicator */}
                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {(shop.subscription_tier === 'professional' || shop.subscription_tier === 'pro' || shop.subscription_tier === 'enterprise') ? (
                        <span style={{
                          background: 'rgba(34, 197, 94, 0.15)',
                          color: '#22c55e',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          ✓ Online Booking Available
                        </span>
                      ) : (
                        <span style={{
                          background: 'rgba(245, 158, 11, 0.15)',
                          color: '#f59e0b',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px'
                        }}>
                          Phone booking only
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
            {filteredShops.length} {filteredShops.length === 1 ? 'shop' : 'shops'} found
          </span>
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShopBrowserModal;
